"use client";

import { useCallback, useEffect, useState } from "react";
import {
  INTERNET_COMPARE_LIMIT,
  INTERNET_PLAN_ID_PATTERN,
} from "@/lib/fetch/internet";

const STORAGE_KEY = "luntero:internet-compare";
const CHANGE_EVENT = "luntero:internet-compare-change";

const readSelection = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed
          .filter(
            (id): id is string =>
              typeof id === "string" && INTERNET_PLAN_ID_PATTERN.test(id),
          )
          .slice(0, INTERNET_COMPARE_LIMIT)
      : [];
  } catch {
    return [];
  }
};

const writeSelection = (ids: string[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage can be unavailable (private mode); the selection still works in memory.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: ids }));
};

/**
 * The plans a visitor picked for side-by-side comparison. Persisted per
 * browser and shared by every plan card, the tray and the compare page.
 */
export const useInternetCompare = () => {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    setIds(readSelection());

    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<string[]>).detail;
      setIds(Array.isArray(detail) ? detail : readSelection());
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setIds(readSelection());
    };

    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const replace = useCallback((next: string[]) => {
    writeSelection(
      Array.from(new Set(next)).slice(0, INTERNET_COMPARE_LIMIT),
    );
  }, []);

  const toggle = useCallback((id: string) => {
    const current = readSelection();
    if (current.includes(id)) {
      writeSelection(current.filter((item) => item !== id));
    } else if (current.length < INTERNET_COMPARE_LIMIT) {
      writeSelection([...current, id]);
    }
  }, []);

  const remove = useCallback((id: string) => {
    writeSelection(readSelection().filter((item) => item !== id));
  }, []);

  const clear = useCallback(() => writeSelection([]), []);

  return {
    ids,
    has: (id: string) => ids.includes(id),
    isFull: ids.length >= INTERNET_COMPARE_LIMIT,
    toggle,
    remove,
    replace,
    clear,
  };
};

export const getInternetCompareHref = (ids: string[]) =>
  ids.length > 0
    ? `/resource/internet/compare?plans=${ids.join(",")}`
    : "/resource/internet/compare";
